import { useFullscreenToggle, useTogglePopoverModal } from '@lessonup/client-integration';
import {
  isPrepView,
  PinModalPopover,
  PlayerBarBottom,
  PlayerBarBottomButton,
  PlayerBarBottomNavigation,
  PlayerBarBottomSection,
  useAssignmentId,
  useAssignmentPins,
  useCurrentNavigationState,
  useCurrentPinId,
  useCurrentStudents,
  useCurrentView,
  useFocusMode,
  useGetPinThemeById,
  useNavigation,
  useNonNullAssignment,
  usePlayerDispatcher,
} from '@lessonup/players-modern';
import {
  IconClassroom,
  IconClose,
  IconFullscreenExit,
  IconFullscreenOpen,
  IconResizeMin,
  IconSettings,
  styled,
  useModal,
} from '@lessonup/ui-components';
import React, { useRef } from 'react';
import { useBoolean } from 'usehooks-ts';
import { Pincode } from '../../../../components/PlayerBar/PlayerBarTop/components/Pincode';
import { usePlayerTranslation } from '../../hooks/usePlayerTranslation';
import { PlayerBarBottomComponentsSection } from './PlayerBarBottomComponentsSection';
import { PlayerBarBottomHandlerProps } from './PlayerBarBottomHandler.utils';
import { PlayerBarBottomSettings } from './PlayerBarBottomSettings';
import { PlayerBarBottomFocusModeControls } from './components/PlayerBarBottomFocusModeControls/PlayerBarBottomFocusModeControls';
import { PlayerBarBarBottomInteractiveStepControls } from './components/PlayerBarBottomInteractiveStepControls/PlayerBarBottomInteractiveStepControls';
import { StatusIndicator } from './components/StatusIndicator';

export interface RealtimePlayerBarBottomHandlerProps {
  handleShowJoinScreenModal?: () => void;
}

export const RealtimePlayerBarBottomHandler: React.FC<
  RealtimePlayerBarBottomHandlerProps &
    Pick<PlayerBarBottomHandlerProps, 'showLabels' | 'onClose' | 'withPresenterMode'>
> = ({ handleShowJoinScreenModal, onClose, withPresenterMode }) => {
  const { t } = usePlayerTranslation();
  const dispatcher = usePlayerDispatcher();
  const assignment = useNonNullAssignment();
  const pins = useAssignmentPins();
  const assignmentId = useAssignmentId();
  const currentPinId = useCurrentPinId();
  const navState = useCurrentNavigationState();
  const currentView = useCurrentView();
  const focusMode = useFocusMode();
  const totalStudentCount = useCurrentStudents().length;
  const [isFullscreen, toggleFullscreen] = useFullscreenToggle();
  const { toNextSlide, toPreviousSlide } = useNavigation(dispatcher);
  const getPinThemeById = useGetPinThemeById();

  // Remove after cleaning up the preview player
  const minimizedBar = true;
  const mirroredBar = useBoolean(false);
  const isMirrored = minimizedBar && mirroredBar.value;
  const settingsModal = useModal(PlayerBarBottomSettings);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const [handleSettingsModelClick, handleSettingsModelMouseDown, isSettingsModalVisible] = useTogglePopoverModal(
    settingsModal,
    {
      customModalShow: () => {
        settingsModal.show({
          direction: 'top',
          modalAnchor: 'center',
          allowNewPlayers: assignment.settings.playerSettings.allowNewPlayers,
          assignmentId: assignmentId,
          parentSelector: () => settingsButtonRef.current,
          mirrored: mirroredBar,
          dispatcher,
          visibleSettings: {
            openPresenterMode: withPresenterMode,
            allowNewPlayers: true,
            mirrored: true,
          },
        });
      },
    }
  );

  const showNavigation = !focusMode;
  const showFocusModeSection = !!focusMode;
  const showCloseSection = !focusMode;
  const showInteractiveStepControls = !focusMode;

  // Presentational navigation wiring: provide slide modal handlers and ref
  const pinModal = useModal(PinModalPopover);
  const slidesButtonRef = useRef<HTMLButtonElement>(null);
  const [slideButtonHandleClick, slideButtonHandleMouseDown, isSlidesModalVisible] = useTogglePopoverModal(pinModal, {
    customModalShow: () =>
      pins &&
      pinModal.show({
        pins,
        parentSelector: () => slidesButtonRef.current,
        activePinId: currentPinId,
        direction: 'top',
        modalAnchor: 'center',
        onPinClick: (pinId: string) => dispatcher({ type: 'jump', pinId }),
        getPinThemeByPinId: getPinThemeById,
      }),
  });

  return (
    <PlayerBarBottom minimized={minimizedBar} mirrored={isMirrored}>
      {showNavigation && (
        <PlayerBarBottomSection mirrored={isMirrored}>
          <PlayerBarBottomNavigation
            onNextClick={toNextSlide}
            onPreviousClick={toPreviousSlide}
            onSlidesClick={slideButtonHandleClick}
            onSlidesMouseDown={slideButtonHandleMouseDown}
            slidesButtonRef={slidesButtonRef}
            labelPrevious={t('labels.previous')}
            labelSlides={t('labels.slides')}
            labelNext={t('labels.next')}
            index={pins.findIndex((p) => p.id === currentPinId) + 1}
            total={pins.length}
            hasPrevious={navState.hasPrevious}
            hasNext={navState.hasNext}
            slidesActive={isSlidesModalVisible}
          />
        </PlayerBarBottomSection>
      )}

      {showFocusModeSection && (
        <PlayerBarBottomSection mirrored={isMirrored}>
          <PlayerBarBottomButton
            onClick={() => dispatcher({ type: 'setFocusMode', focusMode: null })}
            label="Back to Slide"
            buttonType="secondary"
            iconStart={<IconResizeMin />}
            active={true}
          >
            {t('focusMode.backToSlide')}
          </PlayerBarBottomButton>
        </PlayerBarBottomSection>
      )}

      <PlayerBarBottomSection mirrored={isMirrored}>
        <Pincode
          hidePincode={!assignment.settings.playerSettings.allowNewPlayers}
          pincode={assignment.pincode ?? ''}
          onClick={handleShowJoinScreenModal}
        />
        <PlayerBarBottomButton
          ref={settingsButtonRef}
          active={isSettingsModalVisible}
          onClick={handleSettingsModelClick}
          onMouseDown={handleSettingsModelMouseDown}
          label={t('labels.settings')}
          iconStart={<IconSettings />}
          tipped
        />
        <PlayerBarBottomButton
          onClick={toggleFullscreen}
          label={isFullscreen ? t('labels.exitFullscreen') : t('labels.fullscreen')}
          iconStart={isFullscreen ? <IconFullscreenExit /> : <IconFullscreenOpen />}
          tipped
        />
      </PlayerBarBottomSection>
      <PlayerBarBottomSection>
        <PlayerBarBottomButton
          disabled={isPrepView(currentView)}
          onClick={handleShowJoinScreenModal}
          label={t('labels.classRoom')}
          iconStart={<IconClassroom />}
          tipped
        >
          <TotalStudentsWrapper>{totalStudentCount}</TotalStudentsWrapper>
        </PlayerBarBottomButton>
      </PlayerBarBottomSection>
      {!focusMode && <PlayerBarBottomComponentsSection />}
      {showInteractiveStepControls && (
        <PlayerBarBarBottomInteractiveStepControls
          currentView={currentView}
          previousStep={toPreviousSlide}
          nextStep={toNextSlide}
        />
      )}
      {focusMode && <PlayerBarBottomFocusModeControls focusMode={focusMode} />}
      <PlayerBarBottomSection gapped mirrored={isMirrored}>
        <StatusIndicator />
        {showCloseSection && (
          <PlayerBarBottomButton onClick={onClose} label={t('labels.closeTip')} iconStart={<IconClose />} tipped>
            {t('labels.close')}
          </PlayerBarBottomButton>
        )}
      </PlayerBarBottomSection>
    </PlayerBarBottom>
  );
};

const TotalStudentsWrapper = styled.div`
  width: 2ch;
`;
